/**
 * The treasury desk's demo state.
 *
 * Its own module rather than more of `data.ts`, which is already two thousand lines and is
 * read by everything.
 *
 * Deliberately not all-green. A dashboard whose every figure is healthy teaches an operator
 * nothing about what the screen looks like when something is wrong, and the review queue and
 * the open delta are the two things this console exists to surface — so the seed ships one
 * order held for review, one custodian balance that has never been read, and one unresolved
 * delta.
 */
import type {
  MoneyDTO,
  RateDTO,
  AdminBridgeLeg,
  AdminTreasuryOrder,
  ReconciliationDelta,
  ReconciliationRun,
  TreasuryHolding,
  TreasuryRebalance,
  TreasuryThresholds,
} from '../../contracts/types.js';

/** Minor-unit exponents for the tickers this demo uses. */
const DECIMALS: Record<string, number> = { NGN: 2, USDT: 6, BTC: 8, ETH: 18, SOL: 9 };

/**
 * The effective price of a pair of amounts, as an exact rational.
 *
 * Scaled by both currencies' decimals for the same reason the API scales it: `Rate.convert`
 * divides by `10^fromDecimals` and multiplies by `10^toDecimals`, so a rate meant to reproduce
 * these very amounts has to carry both. Without the scaling a USDT→NGN rate is out by four
 * orders of magnitude and the console prints a confidently wrong price.
 */
export function rateBetweenDTO(from: MoneyDTO, to: MoneyDTO): RateDTO {
  const fromScale = 10n ** BigInt(DECIMALS[from.currency] ?? 2);
  const toScale = 10n ** BigInt(DECIMALS[to.currency] ?? 2);
  const rawNumerator = BigInt(to.amount) * fromScale;
  const rawDenominator = BigInt(from.amount) * toScale;
  // A rate is required to be strictly positive by the contract, so a zero leg degrades to 1/1
  // rather than producing a value the schema would reject.
  const numerator = rawNumerator === 0n ? 1n : rawNumerator;
  const denominator = rawDenominator === 0n ? 1n : rawDenominator;
  const divisor = gcd(numerator, denominator);
  return {
    from: from.currency,
    to: to.currency,
    numerator: (numerator / divisor).toString(),
    denominator: (denominator / divisor).toString(),
  };
}

function gcd(a: bigint, b: bigint): bigint {
  let x = a < 0n ? -a : a;
  let y = b < 0n ? -b : b;
  while (y !== 0n) [x, y] = [y, x % y];
  return x === 0n ? 1n : x;
}

export function seedTreasuryThresholds(): TreasuryThresholds {
  return {
    // Ships ON, exactly as the API's own defaults do: bridge legs settle on our own books and
    // no vendor is called until an operator deliberately turns this off.
    sandbox: true,
    usdtLowThreshold: { currency: 'USDT', amount: '500000000' },
    ngnFloatLowThreshold: { currency: 'NGN', amount: '5000000000' },
    reconciliationToleranceBps: 10,
    manualReviewCeiling: { currency: 'NGN', amount: '50000000' },
    topUpWindowHours: 24,
    repriceToleranceBps: 100,
    balanceMaxAgeSeconds: 120,
    conversionsPaused: false,
    pausedReason: '',
  } as TreasuryThresholds;
}

export function seedTreasuryHoldings(): TreasuryHolding[] {
  const now = new Date().toISOString();
  return [
    {
      currency: 'USDT',
      held: { currency: 'USDT', amount: '1284500000' },
      owed: { currency: 'USDT', amount: '910200000' },
      free: { currency: 'USDT', amount: '374300000' },
      ledgerTreasury: { currency: 'USDT', amount: '1284500000' },
      fetchedAt: now,
      stale: false,
      low: false,
    },
    {
      currency: 'BTC',
      held: { currency: 'BTC', amount: '4180000' },
      owed: { currency: 'BTC', amount: '3925000' },
      free: { currency: 'BTC', amount: '255000' },
      ledgerTreasury: { currency: 'BTC', amount: '4180000' },
      fetchedAt: now,
      stale: false,
      low: false,
    },
    {
      // Never read successfully — the state a gate refuses on. Present so the screen's
      // "not reported" rendering is exercised rather than only its happy path.
      currency: 'ETH',
      held: null,
      owed: { currency: 'ETH', amount: '1400000000000000000' },
      free: null,
      ledgerTreasury: { currency: 'ETH', amount: '0' },
      fetchedAt: null,
      stale: true,
      low: false,
    },
  ] as TreasuryHolding[];
}

function demoLeg(position: number, leg: string, from: MoneyDTO, to: MoneyDTO): AdminBridgeLeg {
  const stamp = new Date().toISOString();
  return {
    position,
    leg,
    status: 'completed',
    from,
    to,
    rate: rateBetweenDTO(from, to),
    id: `leg-${position}`,
    provider: 'sandbox',
    providerReference: `SBX-${position}00${position}`,
    providerFee: { currency: from.currency, amount: '0' },
    networkFee: { currency: from.currency, amount: '0' },
    ledgerEntryId: null,
    rawPayload: null,
    failureReason: null,
    createdAt: stamp,
    completedAt: stamp,
  } as unknown as AdminBridgeLeg;
}

export function seedTreasuryOrders(): AdminTreasuryOrder[] {
  const now = new Date().toISOString();
  return [
    {
      id: 'tr-1001',
      reference: 'TRX-8f2c91aa7d',
      userId: 'usr-1',
      customer: {
        displayName: 'Ada Umeh',
        email: 'ada@example.com',
        phone: '+234 803 *** 2290',
        reference: 'GX4471902',
      },
      side: 'buy',
      status: 'completed',
      publicStatus: 'completed',
      ngnAmount: { currency: 'NGN', amount: '5000000' },
      coinAmount: { currency: 'USDT', amount: '29150000' },
      usdtAmount: { currency: 'USDT', amount: '29150000' },
      rate: rateBetweenDTO(
        { currency: 'NGN', amount: '5000000' },
        { currency: 'USDT', amount: '29150000' },
      ),
      ngnUsdtRate: rateBetweenDTO(
        { currency: 'NGN', amount: '5000000' },
        { currency: 'USDT', amount: '29150000' },
      ),
      usdtCoinRate: { from: 'USDT', to: 'USDT', numerator: '1', denominator: '1' },
      spreadBps: 75,
      feeBps: 50,
      fee: { currency: 'NGN', amount: '25000' },
      fiatMode: 'wallet',
      reviewReason: null,
      failureReason: null,
      legs: [
        demoLeg(
          1,
          'ngn_in',
          { currency: 'NGN', amount: '5000000' },
          { currency: 'NGN', amount: '5000000' },
        ),
        demoLeg(
          2,
          'usdt_buy',
          { currency: 'NGN', amount: '5000000' },
          { currency: 'USDT', amount: '29150000' },
        ),
      ],
      externalFees: [],
      createdAt: now,
      completedAt: now,
    },
    {
      /*
       * The one waiting on a human, and deliberately an `overpaid` sell.
       *
       * That is the review reason that carries a received amount, so it is the only kind on
       * which the re-price decision is meaningful — the seed exists to let an operator try the
       * decision that actually changes what a customer is paid, rather than only the two that
       * do not.
       */
      id: 'tr-1002',
      reference: 'TRX-31b7de0c42',
      userId: 'usr-2',
      customer: {
        displayName: 'Bayo Adeleke',
        email: null,
        phone: '+234 701 *** 5512',
        reference: 'GX9920318',
      },
      side: 'sell',
      status: 'overpaid',
      publicStatus: 'processing',
      ngnAmount: { currency: 'NGN', amount: '17400000' },
      coinAmount: { currency: 'USDT', amount: '10000000' },
      usdtAmount: { currency: 'USDT', amount: '10000000' },
      rate: rateBetweenDTO(
        { currency: 'USDT', amount: '10000000' },
        { currency: 'NGN', amount: '17400000' },
      ),
      ngnUsdtRate: rateBetweenDTO(
        { currency: 'USDT', amount: '10000000' },
        { currency: 'NGN', amount: '17400000' },
      ),
      usdtCoinRate: { from: 'USDT', to: 'USDT', numerator: '1', denominator: '1' },
      spreadBps: 75,
      feeBps: 50,
      fee: { currency: 'NGN', amount: '87000' },
      fiatMode: 'bank',
      reviewReason: 'overpaid',
      failureReason: null,
      legs: [
        demoLeg(
          1,
          'coin_in',
          { currency: 'USDT', amount: '12500000' },
          { currency: 'USDT', amount: '12500000' },
        ),
      ],
      externalFees: [
        {
          kind: 'psp',
          provider: 'paystack',
          amount: { currency: 'NGN', amount: '5000' },
          note: 'payout fee',
        },
      ],
      createdAt: now,
      completedAt: null,
    },
  ] as unknown as AdminTreasuryOrder[];
}

export function seedTreasuryRebalances(): TreasuryRebalance[] {
  const from: MoneyDTO = { currency: 'NGN', amount: '1500000000' };
  const to: MoneyDTO = { currency: 'USDT', amount: '860000000' };
  return [
    {
      id: 'rb-1',
      reference: 'RBL-000001',
      kind: 'otc_buy',
      from,
      to,
      rate: rateBetweenDTO(from, to),
      counterparty: 'Desk OTC',
      note: 'Weekly USDT top-up',
      recordedBy: 'finance@genex.pay',
      createdAt: new Date(Date.now() - 86_400_000).toISOString(),
    },
  ] as unknown as TreasuryRebalance[];
}

export function seedReconciliationRuns(): ReconciliationRun[] {
  return [
    {
      id: 'run-1',
      scope: 'full',
      status: 'deltas',
      startedAt: new Date(Date.now() - 3_600_000).toISOString(),
      finishedAt: new Date(Date.now() - 3_570_000).toISOString(),
      checked: 214,
      deltas: 1,
      paused: false,
      note: '',
    },
  ] as unknown as ReconciliationRun[];
}

export function seedReconciliationDeltas(): ReconciliationDelta[] {
  return [
    {
      id: 'dl-1',
      runId: 'run-1',
      kind: 'custody_vs_ledger',
      currency: 'BTC',
      expected: '4180000',
      actual: '4179200',
      delta: '-800',
      externalId: 'nowpayments:balance',
      detail: 'The custodian reports 0.000008 BTC less than the ledger claims.',
      resolvedAt: null,
      createdAt: new Date(Date.now() - 3_570_000).toISOString(),
    },
  ] as unknown as ReconciliationDelta[];
}
